import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Storable} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {IngredientMeasurementUnit} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BioentitySearchInputWithItems} from 'inventory/BioentitySearchInput';
import StorableChip from 'inventory/chips/StorableChip';
import MetadataTableContent, {
  ColumnWidth,
  MetadataTableColumnDefinition,
} from 'metadata/MetadataTableContent';
import MetadataTableHeader from 'metadata/MetadataTableHeader';
import {bioentityIdToTypename} from 'registry/graphql/GraphQLBioentityTypes';
import {ORDERED_INGREDIENT_UNITS_FOR_DISPLAY} from 'registry/mixtures/MixtureConstants';
import {StorableSearchInputWithItems} from 'samples/storables/StorableSearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {Sublabel} from 'ui-library/Headers';
import AddIcon from 'ui-library/icons/AddIcon';
import FormulaIcon from 'ui-library/icons/FormulaIcon';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

import {IngredientGraphQL} from '../../../../graphql-fragments/registry/mixtures/__generated/IngredientGraphQL';
import {EditingIngredient, PresentationalIngredientTableProps} from '../MixtureConstants';
import {CatalogIdentifierInput} from './CatalogIdentifierInput';
import {AmountInput, IngredientTableSubheader, UnitsSelector} from './IngredientTableSubheader';
import IngredientTableTitle from './IngredientTableTitle';

type ComponentLotValues = IngredientGraphQL['componentLotValues'];
type ComponentLotContainer = ComponentLotValues['container'];

function IngredientTableColumnHeader(props: {children: JSX.Element}): JSX.Element {
  return (
    <div className={classNames(css(styles.ingredientTableHeaderCell), 'simpleDivTable-headerCell')}>
      <Sublabel className={css(styles.ingredientTableColumnHeader)}>{props.children}</Sublabel>
    </div>
  );
}

function formattedTableColumnContent(children: string | JSX.Element): JSX.Element {
  return <div className={classNames('simpleDivTable-cell')}>{children}</div>;
}

export function PresentationalIngredientTable(props: PresentationalIngredientTableProps): JSX.Element {
  const allowMeasuredIngredients = Boolean(props.tagSchema.mixtureSchemaConfig?.allowMeasuredIngredients);

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (!props.isLoading) {
      if (keyboard.isEnterKey(e)) {
        e.preventDefault();
        props.onCompleteEdit();
      } else if (keyboard.isEscapeKey(e)) {
        e.preventDefault();
        props.draftState.stopEditing();
      }
    }
  };

  const renderComponentBioentityColumnContent = (
    ingredientIndex: number,
    componentBioentity: {id: string} | null,
    isInherited: boolean
  ): JSX.Element => {
    if (!props.isLoading && props.draftState.isEditing && !isInherited) {
      return (
        <BioentitySearchInputWithItems
          autoFocus={false}
          chipSize={ChipSize.SMALL}
          onChange={(bioentities: Array<BioentitySummary>) => {
            let newComponentBioentity = null;
            if (bioentities.length > 0) {
              const id = bioentities[0].id;
              newComponentBioentity = {id, __typename: bioentityIdToTypename(id)};
            }
            props.onUpdateIngredient(ingredientIndex, {
              componentBioentity: newComponentBioentity,
            });
          }}
          selectedItems={componentBioentity == null ? [] : [componentBioentity]}
          shouldAllowMultiple={false}
          onKeyDown={handleKeyDown}
        />
      );
    } else {
      return <BioentityChip id={componentBioentity!.id} size={ChipSize.SMALL} />;
    }
  };

  const renderUnitsColumnContent = (
    ingredientIndex: number,
    ingredientUnits: IngredientMeasurementUnit,
    isInherited: boolean
  ): JSX.Element => {
    function ingredientUnitToDisplayText(unit: IngredientMeasurementUnit): string {
      return props.isLoading || allowMeasuredIngredients
        ? unit
        : `${unit}/${props.draftState.data.mixtureUnits}`;
    }

    if (!props.isLoading && props.draftState.isEditing && !isInherited) {
      return (
        <UnitsSelector
          validUnits={ORDERED_INGREDIENT_UNITS_FOR_DISPLAY}
          currentUnits={ingredientUnits}
          getUnitDisplayText={ingredientUnitToDisplayText}
          onSelectUnits={(selectedUnits) => {
            props.onUpdateIngredient(ingredientIndex, {units: selectedUnits});
          }}
          disabled={!props.draftState.isEditing}
        />
      );
    } else {
      return <span>{ingredientUnitToDisplayText(ingredientUnits)}</span>;
    }
  };

  const renderCatalogIdentifierColumnContent = (
    ingredientIndex: number,
    componentBioentity: {id: string} | null,
    catalogIdentifier: string | null,
    isCatalogIdentifierInherited: boolean | null
  ): JSX.Element => {
    if (!props.isLoading && props.draftState.isEditing && !isCatalogIdentifierInherited) {
      return (
        <CatalogIdentifierInput
          componentBioentity={componentBioentity}
          onChange={(e) => {
            props.onUpdateIngredient(ingredientIndex, {catalogIdentifier: e.target.value});
          }}
          value={catalogIdentifier}
          onKeyDown={handleKeyDown}
        />
      );
    } else {
      return <span>{catalogIdentifier}</span>;
    }
  };

  const renderComponentLotTextColumnContent = (
    ingredientIndex: number,
    componentLotValues: ComponentLotValues
  ): JSX.Element => {
    if (!props.isLoading && props.draftState.isEditing) {
      return (
        <TextInput
          className={css(styles.textInput)}
          size={Size.SMALL}
          value={componentLotValues.text || ''}
          onChange={(e) => {
            props.onUpdateIngredient(ingredientIndex, {
              componentLotValues: {...componentLotValues, text: e.target.value},
            });
          }}
          onKeyDown={handleKeyDown}
          disabled={!props.draftState.isEditing}
        />
      );
    } else {
      return <span>{componentLotValues.text}</span>;
    }
  };

  const hasValueForComponentLot = (ingredientIndex: number, valueKey: 'container' | 'bioentity'): boolean =>
    Boolean(
      !props.isLoading && props.draftState.data.ingredients[ingredientIndex].componentLotValues[valueKey]
    );

  const renderComponentLotBioentityColumnContent = (
    ingredientIndex: number,
    componentLotValues: ComponentLotValues
  ): JSX.Element | '' => {
    if (!props.isLoading && props.draftState.isEditing) {
      // Disabled if user has selected a component lot container, re-enables if they remove it
      const disabled: boolean = hasValueForComponentLot(ingredientIndex, 'container');
      return (
        <BioentitySearchInputWithItems
          autoFocus={false}
          chipSize={ChipSize.SMALL}
          onChange={(bioentities: Array<BioentitySummary>) => {
            let newComponentLotBioentity = null;
            if (bioentities.length > 0) {
              const id = bioentities[0].id;
              newComponentLotBioentity = {id, __typename: bioentityIdToTypename(id)};
            }
            props.onUpdateIngredient(ingredientIndex, {
              componentLotValues: {...componentLotValues, bioentity: newComponentLotBioentity},
            });
          }}
          selectedItems={componentLotValues.bioentity == null ? [] : [componentLotValues.bioentity]}
          shouldAllowMultiple={false}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          isReadOnly={disabled}
        />
      );
    } else {
      return componentLotValues.bioentity ? (
        <BioentityChip id={componentLotValues.bioentity.id} size={ChipSize.SMALL} />
      ) : (
        ''
      );
    }
  };

  const renderComponentLotContainerColumnContent = (
    ingredientIndex: number,
    componentLotValues: ComponentLotValues
  ): JSX.Element | '' => {
    if (!props.isLoading && props.draftState.isEditing) {
      // Disabled if user has selected a component lot bioentity, re-enables if they remove it
      const disabled =
        !hasValueForComponentLot(ingredientIndex, 'container') &&
        hasValueForComponentLot(ingredientIndex, 'bioentity');
      return (
        <StorableSearchInputWithItems
          autoFocus={false}
          chipSize={ChipSize.SMALL}
          onChange={(storables: Array<Storable>) => {
            let newComponentLotContainer: ComponentLotContainer | null = null;
            if (storables.length > 0) {
              newComponentLotContainer = {
                id: storables[0].id,
                __typename: 'ContainerNode',
              };
            }
            props.onUpdateIngredient(ingredientIndex, {
              componentLotValues: {...componentLotValues, container: newComponentLotContainer},
            });
          }}
          selectedItems={componentLotValues.container == null ? [] : [componentLotValues.container]}
          shouldAllowMultiple={false}
          storableTypes={[ModelTypeConstants.CONTAINER]}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          isReadOnly={disabled}
        />
      );
    } else {
      return componentLotValues.container ? (
        <StorableChip id={componentLotValues.container.id} size={ChipSize.SMALL} />
      ) : (
        ''
      );
    }
  };

  const renderAmountColumnContent = (
    ingredientIndex: number,
    ingredientAmount: string | null
  ): JSX.Element => {
    const amountString = ingredientAmount || '';
    if (!props.isLoading && props.draftState.isEditing) {
      return (
        <AmountInput
          currentAmount={ingredientAmount}
          onChangeAmount={(newAmount) => {
            props.onUpdateIngredient(ingredientIndex, {amount: newAmount});
          }}
          onKeyDown={handleKeyDown}
          disabled={!props.draftState.isEditing}
        />
      );
    } else {
      return <span>{amountString}</span>;
    }
  };

  const renderInheritedTargetAmountColumnContent = (inheritedTargetAmount: string | null): JSX.Element => (
    <span>{inheritedTargetAmount}</span>
  );

  const renderNotesColumnContent = (ingredientIndex: number, notes: string | null): JSX.Element => {
    if (!props.isLoading && props.draftState.isEditing) {
      return (
        <TextInput
          className={css(styles.textInput)}
          size={Size.MEDIUM}
          value={notes || ''}
          onChange={(e) => {
            props.onUpdateIngredient(ingredientIndex, {notes: e.target.value});
          }}
          onKeyDown={handleKeyDown}
          disabled={!props.draftState.isEditing}
        />
      );
    } else {
      return <span>{notes}</span>;
    }
  };

  const getIngredientColumnDefs = (): Array<MetadataTableColumnDefinition<EditingIngredient>> => {
    const componentBioentityColumnDef = {
      columnHeader: 'Component',
      columnWidth: ColumnWidth.LARGE,
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderComponentBioentityColumnContent(
          rowIndex,
          ingredient.componentBioentity,
          ingredient.isInherited
        ),
    };
    const unitsColumnDef = {
      columnHeader: 'Units',
      columnWidth: ColumnWidth.DEFAULT,
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderUnitsColumnContent(rowIndex, ingredient.units, ingredient.isInherited),
    };
    const catalogIdColumnDef = {
      columnHeader: 'Catalog #',
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderCatalogIdentifierColumnContent(
          rowIndex,
          ingredient.componentBioentity,
          ingredient.catalogIdentifier,
          ingredient.isCatalogIdentifierInherited
        ),
    };
    const componentLotTextColumnDef = {
      columnHeader: 'Lot Text',
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderComponentLotTextColumnContent(rowIndex, ingredient.componentLotValues),
    };
    const componentLotBioentityColumnDef = {
      columnHeader: 'Lot Entity',
      columnWidth: ColumnWidth.LARGE,
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderComponentLotBioentityColumnContent(rowIndex, ingredient.componentLotValues),
    };
    const componentLotContainerColumnDef = {
      columnHeader: 'Lot Container',
      columnWidth: ColumnWidth.LARGE,
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderComponentLotContainerColumnContent(rowIndex, ingredient.componentLotValues),
    };
    const amountColumnDef = {
      columnHeader: allowMeasuredIngredients ? 'Measured Amount' : 'Target Amount',
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderAmountColumnContent(rowIndex, ingredient.amount),
    };
    const inheritedTargetAmountColumnDef = {
      columnHeader: (
        <div>
          {'Target Amount '}
          {allowMeasuredIngredients && (
            <FormulaIcon className="u-faded u-normal-font-weight u-no-text-transform" />
          )}
        </div>
      ),
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderInheritedTargetAmountColumnContent(ingredient.inheritedTargetAmount),
    };

    const notesColumnDef = {
      columnHeader: 'Notes',
      columnWidth: ColumnWidth.LARGE,
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        renderNotesColumnContent(rowIndex, ingredient.notes),
    };

    const columnDefs = allowMeasuredIngredients
      ? [
          componentBioentityColumnDef,
          catalogIdColumnDef,
          inheritedTargetAmountColumnDef,
          unitsColumnDef,
          amountColumnDef,
          ...(props.tagSchema.mixtureSchemaConfig?.componentLotTextEnabled
            ? [componentLotTextColumnDef]
            : []),
          ...(props.tagSchema.mixtureSchemaConfig?.componentLotStorageEnabled
            ? [componentLotContainerColumnDef, componentLotBioentityColumnDef]
            : []),
          notesColumnDef,
        ]
      : [componentBioentityColumnDef, catalogIdColumnDef, amountColumnDef, unitsColumnDef, notesColumnDef];

    return columnDefs.map(({columnHeader, mapItemToColumnContent, ...defOptions}, columnIndex) => ({
      columnHeader: (
        <IngredientTableColumnHeader>
          <div>
            {columnHeader}
            {props.areIngredientsEditable && columnIndex === 0 && (
              <AddIcon
                disabled={props.isLoading}
                onClick={props.isLoading ? undefined : props.onAddIngredient}
              />
            )}
          </div>
        </IngredientTableColumnHeader>
      ),
      mapItemToColumnContent: (ingredient: EditingIngredient, rowIndex: number) =>
        formattedTableColumnContent(mapItemToColumnContent(ingredient, rowIndex)),
      ...defOptions,
    }));
  };

  return (
    <div
      className="metadata-table simpleDivTable"
      {...getTestProps({component: 'PresentationalIngredientTable', element: 'root'})}
    >
      <MetadataTableHeader
        isEditable={!props.isLoading && props.areIngredientsEditable}
        isEditing={!props.isLoading && props.draftState.isEditing}
        title={
          <IngredientTableTitle
            tagSchemaName={props.tagSchema.name}
            mixtureSchemaConfig={props.tagSchema.mixtureSchemaConfig!}
          />
        }
        headerContent={
          <IngredientTableSubheader
            {...props}
            allowMeasuredIngredients={allowMeasuredIngredients}
            onKeyDown={handleKeyDown}
          />
        }
        onEdit={props.isLoading ? noop : props.draftState.startEditing}
        handleCancelEdit={props.isLoading ? noop : props.draftState.stopEditing}
        handleCompleteEdit={props.isLoading ? noop : props.onCompleteEdit}
      />
      <div className={`${css(styles.scroll)}`}>
        <div className={`${css(styles.maxContent)}`}>
          <MetadataTableContent<EditingIngredient>
            isLoading={props.isLoading}
            items={props.isLoading ? [] : props.draftState.data.ingredients}
            isEmpty={props.isLoading || props.draftState.data.ingredients.length === 0}
            columnDefinitions={getIngredientColumnDefs()}
            emptyMessage="This mixture has no ingredients."
            isEditing={!props.isLoading && props.draftState.isEditing}
            canRemoveRows={props.areIngredientsEditable}
            handleRemoveRow={props.isLoading ? noop : props.onDeleteIngredient}
            isRoomy={true}
            confirmDeleteModalBody={
              <>Are you sure you want to delete this ingredient? This action is irreversible.</>
            }
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  ingredientTableColumnHeader: {
    height: '32px',
    display: 'table-cell',
    verticalAlign: 'bottom',
  },
  ingredientTableHeaderCell: {
    paddingBottom: '4px',
    paddingRight: '4px',
  },
  textInput: {
    display: 'flex',
  },
  scroll: {
    overflow: 'auto',
  },
  maxContent: {
    minWidth: 'max-content',
  },
});
